import { BLUESKY_MAX_GRAPHEMES, X_MAX_CHARS, X_URL_CHARS } from "./limits.js";
import type { SharePlatform } from "./types.js";

/**
 * Share-intent URL builders — pure functions, unit-tested, one source of truth
 * for the ShareBar. Verified against the 2026 state of each network:
 *
 *  - X: `twitter.com/intent/tweet` still works; `x.com/intent/post` has live
 *    redirect/login bugs, so it is never used. X inline-plays plain YouTube
 *    links, so the X button shares the WATCH URL rather than our landing page.
 *  - Facebook: only `u` is honoured; `quote` died in 2023. OG tags do the work.
 *  - WhatsApp: one `text` param carrying caption AND url.
 *  - Telegram: `url` + `text`, both live.
 *  - Bluesky: only `text` (≤300 graphemes INCLUDING the url).
 *  - LinkedIn: only `url`; no caption possible.
 *  - Reddit: `url` + `title`.
 *  - Instagram / TikTok: no web share intent exists at all → copy caption.
 */

export interface ShareInput {
  /** Our landing page for the clip (carries OG meta + tracking params). */
  landingUrl: string;
  /** The clip's YouTube watch URL — what X gets, because it plays inline there. */
  youtubeUrl: string;
  caption: string;
  /** Persian captions must isolate the LTR url so composers don't mangle it. */
  rtl?: boolean;
}

const RLI = "⁧"; // right-to-left isolate
const LRI = "⁦"; // left-to-right isolate
const PDI = "⁩"; // pop directional isolate

/** Caption + url as one string, with the url bidi-isolated when the caption is RTL. */
export function captionWithUrl(caption: string, url: string, rtl = false): string {
  const trimmed = caption.trim();
  if (!trimmed) return url;
  return rtl ? `${RLI}${trimmed}${PDI}\n${LRI}${url}${PDI}` : `${trimmed}\n${url}`;
}

/** Grapheme-cluster count — what Bluesky (and humans) actually count; Persian ZWNJ is not a character. */
export function graphemeCount(text: string): number {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    let n = 0;
    for (const _ of seg.segment(text)) n++;
    return n;
  }
  return Array.from(text).length;
}

/** X counts any url as 23 chars regardless of length. */
export function xLength(caption: string, urlCount = 1): number {
  return Array.from(caption).length + urlCount * (X_URL_CHARS + 1);
}

export function fitsX(caption: string): boolean {
  return xLength(caption) <= X_MAX_CHARS;
}

export function fitsBluesky(caption: string, url: string): boolean {
  return graphemeCount(captionWithUrl(caption, url)) <= BLUESKY_MAX_GRAPHEMES;
}

/**
 * Trim a caption on a word boundary until it satisfies `fits`; appends an
 * ellipsis so a cut caption never reads as complete.
 */
export function fitCaption(caption: string, fits: (c: string) => boolean): string {
  let c = caption.trim();
  if (fits(c)) return c;
  const words = c.split(/\s+/);
  while (words.length > 1) {
    words.pop();
    c = `${words.join(" ")}…`;
    if (fits(c)) return c;
  }
  // A single enormous word: hard-cut by code point.
  let cps = Array.from(c);
  while (cps.length > 1 && !fits(`${cps.join("")}…`)) cps = cps.slice(0, -1);
  return `${cps.join("")}…`;
}

export type IntentPlatform = Exclude<
  SharePlatform,
  "instagram_copy" | "tiktok_copy" | "webshare" | "copy_link"
>;

export function shareIntentUrl(platform: IntentPlatform, input: ShareInput): string {
  const { landingUrl, youtubeUrl, caption, rtl } = input;
  const enc = encodeURIComponent;
  switch (platform) {
    case "telegram":
      return `https://t.me/share/url?url=${enc(landingUrl)}&text=${enc(caption)}`;
    case "whatsapp":
      return `https://wa.me/?text=${enc(captionWithUrl(caption, landingUrl, rtl))}`;
    case "x": {
      const text = fitCaption(caption, fitsX);
      return `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(youtubeUrl)}`;
    }
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${enc(landingUrl)}`;
    case "bluesky": {
      const text = fitCaption(caption, (c) => fitsBluesky(c, landingUrl));
      return `https://bsky.app/intent/compose?text=${enc(captionWithUrl(text, landingUrl, rtl))}`;
    }
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${enc(landingUrl)}`;
    case "reddit":
      return `https://www.reddit.com/submit?url=${enc(landingUrl)}&title=${enc(caption)}`;
  }
}

/**
 * Tracking params on the landing url: compact, lowercase, self-hosted
 * taxonomy (`s` = platform, `c` = clip slug) logged by the API and stripped
 * client-side after recording. Not UTM: a Postgres table is the analytics.
 */
export function trackedLandingUrl(base: string, platform: SharePlatform, clipSlug: string): string {
  const u = new URL(base);
  u.searchParams.set("s", platform);
  u.searchParams.set("c", clipSlug);
  return u.toString();
}

export function youtubeWatchUrl(youtubeId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(youtubeId)}`;
}

/** Privacy-enhanced embed; only ever injected on click (see TrailerFacade). */
export function youtubeEmbedUrl(youtubeId: string, startSeconds?: number): string {
  const u = new URL(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}`);
  u.searchParams.set("autoplay", "1");
  u.searchParams.set("rel", "0");
  if (startSeconds && startSeconds > 0) u.searchParams.set("start", String(Math.floor(startSeconds)));
  return u.toString();
}
